// src/screens/Admin/ActivityGraphDetail.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import './ActivityGraphDetail.css';

import backIcon from '../../assets/backspace-icon.png';
import infoIcon from '../../assets/info-icon.png';

import BarChart from '../../components/charts/BarChart';
import GraphsExplainerDrawer from '../../components/popups/GraphsExplainerDrawer';

export default function ActivityGraphDetail() {
  const navigate = useNavigate();

  // 'today' | '7d' | '30d'
  const [range, setRange] = useState('7d');
  const [buckets, setBuckets] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [winWidth, setWinWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ✅ Full width (we will “full-bleed” it in CSS)
  const chartWidth = Math.min(1100, Math.max(320, winWidth));

  useEffect(() => {
    fetchData(range);
    // eslint-disable-next-line
  }, []);

  const fetchData = async (r) => {
    try {
      setRange(r);
      const res = await api.get(`/admin/metrics/activity-by-hour?range=${r}`);
      setBuckets(res.data?.buckets || []);
    } catch (err) {
      console.error('Failed to load activity-by-hour', err);
      setBuckets([]);
    }
  };

  const subtitle = useMemo(() => {
    if (range === 'today') return 'Unique active users per bucket (today)';
    if (range === '7d') return 'Average unique active users per day (last 7 days)';
    return 'Average unique active users per day (last 30 days)';
  }, [range]);

  // ✅ Tooltip-friendly time range (keeps x-axis short)
  const prettyTimeRange = (label) => {
    const s = String(label || '').trim();

    // Supports: "00-04", "00–04", "00—04"
    const m = s.match(/^(\d{1,2})\s*[-–—]\s*(\d{1,2})$/);
    if (!m) return s;

    const pad2 = (n) => String(n).padStart(2, '0');
    const a = pad2(m[1]);
    const b = pad2(m[2]);

    return `${a}:00 - ${b}:00`;
  };

  const InfoBtn = ({ onClick, label }) => (
    <button
      type="button"
      onClick={onClick}
      className="ag-infoBtn"
      aria-label={label}
      title={label}
    >
      <img src={infoIcon} alt="" className="ag-infoIcon" />
    </button>
  );

  const data = useMemo(() => {
    return (buckets || []).map((b) => ({
      x: b.label, // keep short for x-axis
      y: Number(b.value) || 0,
      meta: b,
    }));
  }, [buckets]);

  return (
    <div className="ag-page">
      <div className="ag-header">
        <img
          src={backIcon}
          alt="Back"
          className="ag-back"
          onClick={() => navigate('/admin/graphs')}
        />
        <span className="ag-title">Activity by Time of Day</span>
        <div className="ag-spacer" />
      </div>

      <div className="ag-scroll">
        <div className="ag-filters">
          <button
            className={range === 'today' ? 'active' : ''}
            onClick={() => fetchData('today')}
          >
            Today
          </button>
          <button
            className={range === '7d' ? 'active' : ''}
            onClick={() => fetchData('7d')}
          >
            7d
          </button>
          <button
            className={range === '30d' ? 'active' : ''}
            onClick={() => fetchData('30d')}
          >
            30d
          </button>
        </div>

        <div className="ag-rows">
          {/* ✅ Full-bleed wrapper so it doesn’t feel “narrow” on mobile */}
          <div className="ag-chartFullBleed">
            <BarChart
              flat
              title="Active Users by Time Bucket"
              subtitle={subtitle}
              titleRight={
                <InfoBtn
                  onClick={() => setDrawerOpen(true)}
                  label="What does this chart mean?"
                />
              }
              data={data}
              width={chartWidth}
              height={280}
              formatX={(x) => x} // keep short label on axis
              formatY={(v) => `${v}`}
              tooltipFormat={({ x, y, meta }) => {
                const label =
                  range === 'today'
                    ? `Active users: ${y}`
                    : `Avg active/day: ${Number(y).toFixed(1)}`;

                return {
                  // ✅ Tooltip shows "00:00 - 04:00"
                  title: `Time: ${prettyTimeRange(x)}`,
                  lines: [
                    label,
                    meta?.avgPerDay != null && range !== 'today'
                      ? `Avg/day (raw): ${Number(meta.avgPerDay).toFixed(2)}`
                      : null,
                  ].filter(Boolean),
                };
              }}
            />
          </div>
        </div>
      </div>

      <GraphsExplainerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        type="activityByHour"
      />
    </div>
  );
}
